import { useEffect } from "react";

import { cookieExtractor } from "@shared/helpers/cookieExtractor/cookieExtractor";
import { isDevelopment } from "@shared/utils/fullscriptEnv/fullscriptEnv";

interface Props {
  googleTagManager?: string;
  googleAnalyticsToken?: string;
}

/**
 * ⚠️ Only use this in unauthenticated parts of the app.
 *
 * These ARE NOT HIPAA compliant
 */
const GoogleServices = ({ googleTagManager, googleAnalyticsToken }: Props) => {
  useEffect(() => {
    if (isDevelopment() || !googleTagManager) return;

    const CONSENT_COOKIE = "STYXKEY_website_consent_preference";
    const cookieRecord = cookieExtractor([CONSENT_COOKIE]) || {};

    if (window.gtag) {
      if (CONSENT_COOKIE in cookieRecord) {
        const consentValue = cookieRecord[CONSENT_COOKIE];
        const isGranted = consentValue.includes("granted");

        window.gtag("consent", "update", {
          ad_storage: isGranted ? "granted" : "denied",
          analytics_storage: isGranted ? "granted" : "denied",
          ad_user_data: isGranted ? "granted" : "denied",
          ad_personalization: isGranted ? "granted" : "denied",
        });
      } else {
        window.gtag("consent", "default", {
          ad_storage: "denied",
          analytics_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
        });
      }
    }
  }, [googleTagManager]);

  const renderGTM = () => {
    if (isDevelopment() || !googleTagManager) return;

    return (
      <>
        <script
          data-testid="gtm-head"
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line @typescript-eslint/naming-convention
            __html: `
          window.addEventListener("load", function() {
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer', '${googleTagManager}');
          })
        `,
          }}
        />
        <script
          data-testid="gtag"
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line @typescript-eslint/naming-convention
            __html: `window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}`,
          }}
        ></script>
      </>
    );
  };

  const renderGA = () => {
    if (isDevelopment() || !googleAnalyticsToken) return;

    return (
      <script
        data-testid="google-analytics"
        dangerouslySetInnerHTML={{
          // eslint-disable-next-line @typescript-eslint/naming-convention
          __html: `
          window.addEventListener("load", function() {
            (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
            (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
            m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
            })(window,document,'script','//www.google-analytics.com/analytics.js','ga');
            ga('create', '${googleAnalyticsToken}', 'auto');
            ga('send', 'pageview');
          });
        `,
        }}
      />
    );
  };

  return (
    <>
      {renderGTM()}
      {renderGA()}
    </>
  );
};

export { GoogleServices };
